import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface FiscalDocumentRow {
  id: string;
  e_ncf: string | null;
  document_type: string;
  environment: string;
  fiscal_status: string;
  payment_status: string | null;
  rejection_reason: string | null;
  dgii_status: string | null;
  internal_number: string | null;
  total: number | null;
  created_at: string;
}

export interface FiscalErrorRow {
  id: string;
  document_id: string | null;
  e_ncf: string | null;
  error_code: string | null;
  error_message: string | null;
  dgii_response: string | null;
  cause: string | null;
  recommended_action: string | null;
  retryable: boolean;
  status: string;
  retry_count: number;
  created_at: string;
}

export interface FiscalCounts {
  emitidosHoy: number;
  total: number;
  pendientes: number;
  enviados: number;
  aceptados: number;
  rechazados: number;
  errores: number;
  contingencias: number;
}

const EMPTY_COUNTS: FiscalCounts = {
  emitidosHoy: 0,
  total: 0,
  pendientes: 0,
  enviados: 0,
  aceptados: 0,
  rechazados: 0,
  errores: 0,
  contingencias: 0,
};

/** Estados de un documento que aún no se ha enviado a DGII. */
const PENDING_SET = new Set(['DRAFT', 'VALIDATING', 'XML_GENERATED', 'SIGNED']);
const SENT_SET = new Set(['PENDING_DGII', 'SENT', 'RECEIVED']);
const ERROR_SET = new Set(['ERROR', 'RETRY']);

/** Carga el resumen fiscal (documentos + indicadores) para el dashboard. */
export function useFiscalDashboard() {
  const [documents, setDocuments] = useState<FiscalDocumentRow[]>([]);
  const [counts, setCounts] = useState<FiscalCounts>(EMPTY_COUNTS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase
        .from('electronic_documents')
        .select(
          'id, e_ncf, document_type, environment, fiscal_status, payment_status, rejection_reason, dgii_status, internal_number, total, created_at'
        )
        .order('created_at', { ascending: false })
        .limit(500);
      if (e) throw e;

      const rows = (data ?? []) as FiscalDocumentRow[];
      setDocuments(rows);

      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);

      const next: FiscalCounts = { ...EMPTY_COUNTS };
      next.total = rows.length;
      for (const d of rows) {
        if (new Date(d.created_at) >= startOfToday) next.emitidosHoy += 1;
        if (PENDING_SET.has(d.fiscal_status)) next.pendientes += 1;
        else if (SENT_SET.has(d.fiscal_status)) next.enviados += 1;
        else if (d.fiscal_status === 'ACCEPTED') next.aceptados += 1;
        else if (d.fiscal_status === 'REJECTED') next.rechazados += 1;
        else if (ERROR_SET.has(d.fiscal_status)) next.errores += 1;
        else if (d.fiscal_status === 'CONTINGENCY') next.contingencias += 1;
      }
      setCounts(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el panel fiscal');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { documents, counts, loading, error, reload: load };
}

/** Carga los errores fiscales (documentos rechazados/erróneos + registro de errores). */
export function useFiscalErrors() {
  const [errorDocuments, setErrorDocuments] = useState<FiscalDocumentRow[]>([]);
  const [errors, setErrors] = useState<FiscalErrorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [docRes, errRes] = await Promise.all([
        supabase
          .from('electronic_documents')
          .select(
            'id, e_ncf, document_type, environment, fiscal_status, rejection_reason, dgii_status, internal_number, total, created_at'
          )
          .in('fiscal_status', ['REJECTED', 'ERROR', 'RETRY'])
          .order('created_at', { ascending: false })
          .limit(200),
        supabase
          .from('fiscal_errors')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(200),
      ]);

      if (docRes.error) throw docRes.error;
      if (errRes.error) throw errRes.error;

      setErrorDocuments((docRes.data ?? []) as FiscalDocumentRow[]);
      setErrors((errRes.data ?? []) as FiscalErrorRow[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el centro de errores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { errorDocuments, errors, loading, error, reload: load };
}